import { ChangeDetectionStrategy, Component, DestroyRef, Inject, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import { AlertService } from '@app/shared/alert.service';
import { TradeDoc } from '@app/shared/constants';
import { PrintService } from '@app/shared/print.service';
import { QuotationService } from '@app/sales/quotation/quotation.service';
import { SignaturePadComponent } from '@app/shared/signature-pad/signature-pad.component';
import { SpinnerService } from '@app/shared/spinner.service';

export interface SignDocData {
  orderId: number;
  doc: TradeDoc; // la pièce que l'écran a décidé de faire signer (état de la commande)
  customerLabel: string;
}

// Le client signe le PDF du serveur, pas l'aperçu HTML (sa mise en page attend la pagination de Chromium) ;
// tant que le PDF n'est pas affiché, rien ne se signe (doc : front.sales.signature-devis-bon-commande).
@Component({
  selector: 'app-sign-doc-dialog',
  standalone: true,
  imports: [MatDialogModule, MatIconModule, MatProgressSpinnerModule, SignaturePadComponent],
  templateUrl: './sign-doc-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./sign-doc-dialog.component.scss'],
})
export class SignDocDialogComponent implements OnInit, OnDestroy {
  @ViewChild(SignaturePadComponent) pad!: SignaturePadComponent;

  public docUrl: SafeResourceUrl | null = null; // le document rendu, vu par l'iframe
  public loading = true;
  public loadError = '';
  public posting = false; // garde anti double-signature pendant l'aller-retour serveur

  private objectUrl = ''; // URL blob de l'aperçu, révoquée à la fermeture
  private destroyRef = inject(DestroyRef);

  constructor(
    public dialogRef: MatDialogRef<SignDocDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SignDocData,
    private printService: PrintService,
    private quotationService: QuotationService,
    private alertService: AlertService,
    private spin: SpinnerService,
    private sanitizer: DomSanitizer,
  ) {}

  ngOnInit(): void {
    // Fermé avant la réponse : la requête est annulée, sinon l'URL blob créée après ngOnDestroy ne serait jamais révoquée.
    this.printService.getOrderDoc$(this.data.orderId, this.data.doc).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      // navpanes=0 : sans lui Chrome ouvre son volet de vignettes (un tiers de la largeur) ; view=FitH ouvre à la largeur de la page.
      next: (blob: Blob) => {
        this.objectUrl = URL.createObjectURL(blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' }));
        this.docUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.objectUrl + '#navpanes=0&view=FitH');
        this.loading = false;
      },
      error: (e: any) => {
        this.loading = false;
        this.loadError = "Le document n'a pas pu être affiché : rien ne peut être signé tant qu'il n'est pas sous les yeux du client.";
        console.error('[SignDocDialogComponent] aperçu du document impossible', e);
      },
    });
  }

  ngOnDestroy(): void {
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
  }

  get docLabel(): string {
    return this.data?.doc === 'devis' ? 'devis' : 'bon de commande';
  }

  get title(): string {
    return this.data?.doc === 'devis' ? 'Signature du devis' : 'Signature du bon de commande';
  }

  signatureValid(): boolean {
    return !!this.docUrl && !!this.pad?.signatureValid();
  }

  clearSignature(): void {
    this.pad?.clear();
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  onSign(): void {
    if (this.posting || !this.signatureValid()) return;
    this.posting = true;
    this.dialogRef.disableClose = true; // Échap pendant l'envoi fermerait sans retour, et une re-signature laisserait un second PDF scellé
    const signature = this.pad.toDataURL();
    const note = this.pad.noteText(); // remarque facultative : imprimée dans le cadre d'acceptation
    // Fermeture APRÈS le serveur : tant que le PDF n'est pas rangé, la signature n'existe pas.
    this.quotationService
      .postTradeDocSignEvent(this.data.orderId, this.data.doc, signature, note)
      .pipe(this.spin.block('Signature du ' + this.docLabel + '…'))
      .subscribe({
        next: (response: any) => this.dialogRef.close(response ?? true),
        error: (e: any) => {
          this.posting = false;
          this.dialogRef.disableClose = false;
          this.alertService.error('ERREUR Serveur. La signature du ' + this.docLabel + ' a échoué');
          console.error(e?.error);
        },
      });
  }
}
